import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { EmailPageLayout } from "@/components/email/EmailPageLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  AlertCircle,
  AlertTriangle,
  CheckCircle2,
  Download,
  Loader2,
  RefreshCcw,
  Search,
  Trash2,
  XCircle,
} from "lucide-react";
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";

// Define types for our data
interface Bounce {
  id: number;
  email: string;
  bounce_type: string;
  bounce_subtype: string;
  timestamp: string;
  diagnostic_code: string;
}

interface Complaint {
  id: number;
  email: string;
  complaint_type: string;
  timestamp: string;
}

interface SuppressionEntry {
  id: number;
  email: string;
  reason: string;
  details: string;
  created_at: string;
}

export default function BounceManagement() {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedTab, setSelectedTab] = useState("bounces");
  const [bounceFilter, setBounceFilter] = useState("all");
  const [complaintFilter, setComplaintFilter] = useState("all");
  const [suppressionFilter, setSuppressionFilter] = useState("all");

  // State for real data
  const [bounceData, setBounceData] = useState<Bounce[]>([]);
  const [complaintData, setComplaintData] = useState<Complaint[]>([]);
  const [suppressionData, setSuppressionData] = useState<SuppressionEntry[]>(
    []
  );
  const [loading, setLoading] = useState(true);

  // Function to fetch data from Supabase
  const fetchData = async () => {
    setLoading(true);

    try {
      // Fetch bounces
      const { data: bounces, error: bounceError } = await supabase
        .from("email_bounces")
        .select("*")
        .order("timestamp", { ascending: false });

      if (bounceError) {
        console.error("Error fetching bounce data:", bounceError);
      }

      // Fetch complaints
      const { data: complaints, error: complaintError } = await supabase
        .from("email_complaints")
        .select("*")
        .order("timestamp", { ascending: false });

      if (complaintError) {
        console.error("Error fetching complaint data:", complaintError);
      }

      // Fetch suppression list
      // Note: In a real implementation, you would have a separate table for the suppression list
      // For now, we'll create a synthetic suppression list from bounces and complaints
      const suppressionEntries: SuppressionEntry[] = [];

      // Add bounces to suppression list
      bounces?.forEach((bounce, index) => {
        if (bounce.bounce_type === "Permanent") {
          suppressionEntries.push({
            id: index + 1,
            email: bounce.email,
            reason: "bounce",
            details: `${bounce.bounce_type}: ${bounce.bounce_subtype}`,
            created_at: bounce.timestamp,
          });
        }
      });

      // Add complaints to suppression list
      complaints?.forEach((complaint, index) => {
        suppressionEntries.push({
          id: suppressionEntries.length + index + 1,
          email: complaint.email,
          reason: "complaint",
          details: complaint.complaint_type || "Unknown",
          created_at: complaint.timestamp,
        });
      });

      // Set state with fetched data
      setBounceData(bounces || []);
      setComplaintData(complaints || []);
      setSuppressionData(suppressionEntries);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch data on component mount
  useEffect(() => {
    fetchData();
  }, []);

  // Filter data based on search term and filters
  const filteredBounces = bounceData.filter((bounce) => {
    const matchesSearch =
      bounce.email.includes(searchTerm) ||
      bounce.diagnostic_code.includes(searchTerm);
    const matchesFilter =
      bounceFilter === "all" ||
      (bounceFilter === "permanent" && bounce.bounce_type === "Permanent") ||
      (bounceFilter === "transient" && bounce.bounce_type === "Transient");
    return matchesSearch && matchesFilter;
  });

  const filteredComplaints = complaintData.filter((complaint) => {
    const matchesSearch = complaint.email.includes(searchTerm);
    const matchesFilter =
      complaintFilter === "all" || complaintFilter === complaint.complaint_type;
    return matchesSearch && matchesFilter;
  });

  const filteredSuppressions = suppressionData.filter((suppression) => {
    const matchesSearch =
      suppression.email.includes(searchTerm) ||
      suppression.details.includes(searchTerm);
    const matchesFilter =
      suppressionFilter === "all" || suppressionFilter === suppression.reason;
    return matchesSearch && matchesFilter;
  });

  const getBounceTypeColor = (type: string) => {
    switch (type) {
      case "Permanent":
        return "destructive";
      case "Transient":
        return "warning";
      default:
        return "secondary";
    }
  };

  const getComplaintTypeColor = (type: string) => {
    switch (type) {
      case "spam":
        return "destructive";
      case "abuse":
        return "destructive";
      case "fraud":
        return "destructive";
      default:
        return "secondary";
    }
  };

  const getSuppressionReasonColor = (reason: string) => {
    switch (reason) {
      case "bounce":
        return "destructive";
      case "complaint":
        return "destructive";
      case "manual":
        return "warning";
      default:
        return "secondary";
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString("en-AU", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <EmailPageLayout
      title="Email Bounce Management"
      description="Monitor and manage email bounces, complaints, and suppression list"
      action={
        <Button
          variant="outline"
          size="sm"
          className="h-8 px-3 text-xs bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-800 hover:bg-blue-100 dark:hover:bg-blue-900/40"
          onClick={fetchData}
          disabled={loading}
        >
          <RefreshCcw
            className={`h-3.5 w-3.5 mr-1.5 ${loading ? "animate-spin" : ""}`}
          />
          {loading ? "Refreshing..." : "Refresh Data"}
        </Button>
      }
    >
      <div className="space-y-6">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div className="flex items-center gap-2 w-full md:w-auto">
            <Search className="h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by email or details..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full md:w-[300px]"
            />
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm">
              <Download className="h-4 w-4 mr-2" />
              Export
            </Button>
            <Button size="sm">Add to Suppression List</Button>
          </div>
        </div>

        <Tabs value={selectedTab} onValueChange={setSelectedTab}>
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="bounces">
              Bounces
              <Badge variant="secondary" className="ml-2">
                {loading ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : (
                  bounceData.length
                )}
              </Badge>
            </TabsTrigger>
            <TabsTrigger value="complaints">
              Complaints
              <Badge variant="secondary" className="ml-2">
                {loading ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : (
                  complaintData.length
                )}
              </Badge>
            </TabsTrigger>
            <TabsTrigger value="suppression">
              Suppression List
              <Badge variant="secondary" className="ml-2">
                {loading ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : (
                  suppressionData.length
                )}
              </Badge>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="bounces" className="space-y-4">
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-2">
                <Label htmlFor="bounce-filter">Filter:</Label>
                <Select value={bounceFilter} onValueChange={setBounceFilter}>
                  <SelectTrigger id="bounce-filter" className="w-[180px]">
                    <SelectValue placeholder="Filter by type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Bounces</SelectItem>
                    <SelectItem value="permanent">Permanent Bounces</SelectItem>
                    <SelectItem value="transient">Transient Bounces</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <Alert variant="default" className="max-w-md">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  Permanent bounces are automatically added to the suppression
                  list.
                </AlertDescription>
              </Alert>
            </div>

            <Card>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Email Address</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Subtype</TableHead>
                      <TableHead>Timestamp</TableHead>
                      <TableHead>Diagnostic Code</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredBounces.length > 0 ? (
                      filteredBounces.map((bounce) => (
                        <TableRow key={bounce.id}>
                          <TableCell className="font-medium">
                            {bounce.email}
                          </TableCell>
                          <TableCell>
                            <Badge
                              variant={getBounceTypeColor(bounce.bounce_type)}
                            >
                              {bounce.bounce_type}
                            </Badge>
                          </TableCell>
                          <TableCell>{bounce.bounce_subtype}</TableCell>
                          <TableCell>{formatDate(bounce.timestamp)}</TableCell>
                          <TableCell
                            className="max-w-[200px] truncate"
                            title={bounce.diagnostic_code}
                          >
                            {bounce.diagnostic_code}
                          </TableCell>
                          <TableCell className="text-right">
                            <Button variant="ghost" size="sm">
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center py-4">
                          No bounce records found
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
              <CardFooter className="flex items-center justify-between border-t p-4">
                <div className="text-sm text-muted-foreground">
                  {loading ? (
                    <div className="flex items-center">
                      <Loader2 className="h-3 w-3 mr-2 animate-spin" />
                      Loading bounce data...
                    </div>
                  ) : (
                    `Showing ${filteredBounces.length} of ${bounceData.length} bounces`
                  )}
                </div>
                <Pagination>
                  <PaginationContent>
                    <PaginationItem>
                      <PaginationPrevious href="#" />
                    </PaginationItem>
                    <PaginationItem>
                      <PaginationLink href="#" isActive>
                        1
                      </PaginationLink>
                    </PaginationItem>
                    <PaginationItem>
                      <PaginationNext href="#" />
                    </PaginationItem>
                  </PaginationContent>
                </Pagination>
              </CardFooter>
            </Card>
          </TabsContent>

          <TabsContent value="complaints" className="space-y-4">
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-2">
                <Label htmlFor="complaint-filter">Filter:</Label>
                <Select
                  value={complaintFilter}
                  onValueChange={setComplaintFilter}
                >
                  <SelectTrigger id="complaint-filter" className="w-[180px]">
                    <SelectValue placeholder="Filter by type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Complaints</SelectItem>
                    <SelectItem value="spam">Spam</SelectItem>
                    <SelectItem value="abuse">Abuse</SelectItem>
                    <SelectItem value="fraud">Fraud</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <Alert variant="destructive" className="max-w-md">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>
                  All complaints are automatically added to the suppression
                  list.
                </AlertDescription>
              </Alert>
            </div>

            <Card>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Email Address</TableHead>
                      <TableHead>Complaint Type</TableHead>
                      <TableHead>Timestamp</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredComplaints.length > 0 ? (
                      filteredComplaints.map((complaint) => (
                        <TableRow key={complaint.id}>
                          <TableCell className="font-medium">
                            {complaint.email}
                          </TableCell>
                          <TableCell>
                            <Badge
                              variant={getComplaintTypeColor(
                                complaint.complaint_type || "unknown"
                              )}
                            >
                              {complaint.complaint_type || "unknown"}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            {formatDate(complaint.timestamp)}
                          </TableCell>
                          <TableCell className="text-right">
                            <Button variant="ghost" size="sm">
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={4} className="text-center py-4">
                          No complaint records found
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
              <CardFooter className="flex items-center justify-between border-t p-4">
                <div className="text-sm text-muted-foreground">
                  {loading ? (
                    <div className="flex items-center">
                      <Loader2 className="h-3 w-3 mr-2 animate-spin" />
                      Loading complaint data...
                    </div>
                  ) : (
                    `Showing ${filteredComplaints.length} of ${complaintData.length} complaints`
                  )}
                </div>
                <Pagination>
                  <PaginationContent>
                    <PaginationItem>
                      <PaginationPrevious href="#" />
                    </PaginationItem>
                    <PaginationItem>
                      <PaginationLink href="#" isActive>
                        1
                      </PaginationLink>
                    </PaginationItem>
                    <PaginationItem>
                      <PaginationNext href="#" />
                    </PaginationItem>
                  </PaginationContent>
                </Pagination>
              </CardFooter>
            </Card>
          </TabsContent>

          <TabsContent value="suppression" className="space-y-4">
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-2">
                <Label htmlFor="suppression-filter">Filter:</Label>
                <Select
                  value={suppressionFilter}
                  onValueChange={setSuppressionFilter}
                >
                  <SelectTrigger id="suppression-filter" className="w-[180px]">
                    <SelectValue placeholder="Filter by reason" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Entries</SelectItem>
                    <SelectItem value="bounce">Bounces</SelectItem>
                    <SelectItem value="complaint">Complaints</SelectItem>
                    <SelectItem value="manual">Manual</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex gap-2">
                <Button variant="outline" size="sm">
                  <CheckCircle2 className="h-4 w-4 mr-2" />
                  Add Email
                </Button>
                <Button variant="destructive" size="sm">
                  <XCircle className="h-4 w-4 mr-2" />
                  Remove Selected
                </Button>
              </div>
            </div>

            <Card>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[40px]">
                        <input
                          type="checkbox"
                          className="rounded border-gray-300"
                        />
                      </TableHead>
                      <TableHead>Email Address</TableHead>
                      <TableHead>Reason</TableHead>
                      <TableHead>Details</TableHead>
                      <TableHead>Added At</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredSuppressions.length > 0 ? (
                      filteredSuppressions.map((suppression) => (
                        <TableRow key={suppression.id}>
                          <TableCell>
                            <input
                              type="checkbox"
                              className="rounded border-gray-300"
                            />
                          </TableCell>
                          <TableCell className="font-medium">
                            {suppression.email}
                          </TableCell>
                          <TableCell>
                            <Badge
                              variant={getSuppressionReasonColor(
                                suppression.reason
                              )}
                            >
                              {suppression.reason}
                            </Badge>
                          </TableCell>
                          <TableCell
                            className="max-w-[200px] truncate"
                            title={suppression.details}
                          >
                            {suppression.details}
                          </TableCell>
                          <TableCell>
                            {formatDate(suppression.created_at)}
                          </TableCell>
                          <TableCell className="text-right">
                            <Button variant="ghost" size="sm">
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center py-4">
                          No suppression list entries found
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
              <CardFooter className="flex items-center justify-between border-t p-4">
                <div className="text-sm text-muted-foreground">
                  {loading ? (
                    <div className="flex items-center">
                      <Loader2 className="h-3 w-3 mr-2 animate-spin" />
                      Loading suppression data...
                    </div>
                  ) : (
                    `Showing ${filteredSuppressions.length} of ${suppressionData.length} suppression entries`
                  )}
                </div>
                <Pagination>
                  <PaginationContent>
                    <PaginationItem>
                      <PaginationPrevious href="#" />
                    </PaginationItem>
                    <PaginationItem>
                      <PaginationLink href="#" isActive>
                        1
                      </PaginationLink>
                    </PaginationItem>
                    <PaginationItem>
                      <PaginationNext href="#" />
                    </PaginationItem>
                  </PaginationContent>
                </Pagination>
              </CardFooter>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </EmailPageLayout>
  );
}
